import { groupBy } from 'lodash'

import { isNonEmptyArray } from '../../../../utils'
import EntityBadge, { CATEGORY_ORDER, getCategoryTranslationKey, getEntityCategory } from '../../Badges/EntityBadge'
import { sortOptionsTree } from './options'

/**
 * Builds the cascader options of a categorized entity selector : one root per non empty category,
 * emitted in rank order, each holding its entities sorted alphabetically.
 *
 * Options carry both a JSX `label` for the display and a plain `name` for everything text based, plus
 * the entity itself under `data` so that a selection can be resolved back.
 *
 * @param {object[]} entities
 * @param {{ t: function, getLabel: function, isDisabled: function }} options
 */
export const buildCategoryTree = (entities, { t, getLabel = entity => entity.name, isDisabled = () => false } = {}) => {
  const categories = groupBy(entities, getEntityCategory)

  return sortOptionsTree(CATEGORY_ORDER
    .filter(category => isNonEmptyArray(categories[category]))
    .map(category => ({
      label: (
        <span className='flex-row ai-ctr' style={{ gap: 5 }}>
          {t(getCategoryTranslationKey(category))}
          <EntityBadge entity={{ [category]: true }} />
        </span>
      ),
      name: t(getCategoryTranslationKey(category)),
      value: category,
      /** A category no entity of which can be picked is not worth expanding. */
      disabled: categories[category].every(isDisabled),
      children: categories[category].map(entity => ({
        label: getLabel(entity),
        name: entity.name,
        value: entity.id,
        data: entity,
        disabled: isDisabled(entity)
      }))
    })))
}

/** Resolves the cascader path leading to an entity, as [category, id]. */
export const getCategoryPath = entity => entity ? [getEntityCategory(entity), entity.id] : []
